import {Config} from '../config';
import {Storage} from '@ionic/storage';
import {Router} from '@angular/router';
import {AuthService} from './auth.service';
import {Injectable, Injector} from '@angular/core';
import {translatable} from '../utils/translateables';
import {Observable, of, throwError, timer} from 'rxjs';
import {TokenStoreService} from './token-store.service';
import {TokenResponseModel} from '../models/auth.model';
import {shcAccessToken} from '../constants/general.constant';
import {catchError, first, map, switchMap} from 'rxjs/operators';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {AlertController, ToastController, NavController} from '@ionic/angular';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {
  private locale: string;
  private translate: TranslateService;
  alert;

  constructor(
    private authService: AuthService,
    private alertCtrl: AlertController,
    private injector: Injector,
    private storage: Storage,
    private toastCtrl: ToastController,
    private navCtrl: NavController,
    private router: Router,
    private tokenStoreService: TokenStoreService) {
    setTimeout(() => {
      this.translate = injector.get(TranslateService);

      this.locale = this.translate.currentLang;

      this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.locale = event.lang;
      });
    });
  }

  async showToast(message) {
    const toast = await this.toastCtrl.create({
      message,
      duration: 5000,
      position: 'top',
      // showCloseButton: true,
      // closeButtonText: 'Done'
    });
    toast.present();
  }

  private reAuthenticate(error: any, expired: boolean = false): Observable<TokenResponseModel> {
    this.authService = this.authService || this.injector.get(AuthService);

    return this.translate.get([
      translatable('Login.Title'),
      translatable('Login.Required'),
      translatable('Login.Expired'),
      translatable('Login.Email'),
      translatable('Login.Password'),
      translatable('Buttons.Login'),
      translatable('Buttons.Cancel')
    ]).pipe(
      switchMap(messages => {
        return this.storage.get('login_email').catch(() => null).then(email => {
          return new Promise<TokenResponseModel>((resolve, reject) => {
            this.alert = this.alertCtrl.create({
              header: messages['Login.Title'],
              message: messages[expired ? 'Login.Expired' : 'Login.Required'],
              inputs: [
                {
                  name: 'email',
                  placeholder: messages['Login.Email'],
                  value: email || '',
                },
                {
                  name: 'password',
                  placeholder: messages['Login.Password'],
                  type: 'password',
                }
              ],
              buttons: [
                {
                  text: messages['Buttons.Cancel'],
                  role: 'cancel',
                  handler: () => {
                    reject();
                  }
                },
                {
                  text: messages['Buttons.Login'],
                  handler: credentials => {
                    if (credentials && credentials.email && credentials.password) {
                      this.authService.login(credentials).subscribe(token => {
                        this.alert.dismiss().then(() => {
                          resolve(token);
                          this.navCtrl.navigateRoot('home');
                        });
                      }, () => {
                        if (error instanceof HttpErrorResponse) {
                          if (error.status === 401) {
                            return this.translate.get(translatable('Login.Errors.NotFound'))
                              .subscribe(message => this.showToast(message));
                          }

                          if (error.status === 422) {
                            if (error.error && error.error.errors) {
                              const keys = Object.keys(error.error.errors);

                              if (keys.length) {
                                const i = 0;
                                let field;
                                let errorMessage;

                                do {
                                  field = error.error.errors[keys[i]];
                                  if (field) {
                                    if (Array.isArray(field) && field.length) {
                                      errorMessage = field[0];
                                      break;
                                    }

                                    if (typeof field === 'string') {
                                      errorMessage = field;
                                      break;
                                    }
                                  }
                                } while (i < keys.length);

                                if (errorMessage) {
                                  return this.translate.get(translatable('Errors.Validation.Message'),
                                    {errorMessage}).subscribe(message => this.showToast(message));
                                }
                              }
                            }

                            return this.translate.get(translatable('Errors.Validation.Generic'))
                              .subscribe(message => this.showToast(message));
                          }

                          if (error.status === 423) {
                            this.translate.get(translatable('Errors.Throttle'))
                              .subscribe(message => this.showToast(message));
                          }
                        }

                        this.translate.get(translatable('Errors.Generic')).subscribe((message) => {
                          return this.showToast(message);
                        });
                      });
                    }

                    return false;
                  }
                }
              ],
              backdropDismiss: false
            });

            return this.alert.alert.present();
          });
        });
      }),
      catchError(() => throwError(error))
    );
  }

  private handle401Response(req: HttpRequest<any>, next: HttpHandler, count: number, error: any): Observable<HttpEvent<any>> {
    this.authService = this.authService || this.injector.get(AuthService);
    this.storage.remove(shcAccessToken);
    this.authService.logout();

    return this.tokenStoreService.token.pipe(
      switchMap((token: any) => {
        //  if (!token) {
        // return this.reAuthenticate(error);
        // }

        return this.authService.refreshToken(token.user.name).pipe(
          catchError(() => of(null)),
          switchMap((tokenResponse: TokenResponseModel) => {
            // if (!tokenResponse) {
            //     return this.reAuthenticate(error, true);
            // }

            return of(tokenResponse);
          })
        );
      }),
      map(token => token.access_token),
      switchMap(token => this.sendRequest(req, next, token, count + 1)),
    );
  }

  private logut401() {
    this.storage.get(shcAccessToken).then(token => {
      this.tokenStoreService.updateToken(null);
      this.authService.logout();
      // this.translate.setDefaultLang('en');
      // this.translate.use('en');
      // this.router.navigate(['welcome'], { replaceUrl: true });
      if (!this.router.url.includes('welcome') && !this.router.url.includes('login') && !this.router.url.includes('register') && !this.router.url.includes('reset') && !this.router.url.includes('newpassword') && !this.router.url.includes('forgot') && !this.router.url.includes('verify')) {
        this.router.navigate(['auth/login'], {replaceUrl: true});
      }
    });
  }

  private sendRequest(req: HttpRequest<any>, next: HttpHandler, token: string = null, count: number = 0): Observable<HttpEvent<any>> {
    if (token) {
      req = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`).set('X-App-Version', Config.appVersion)
      });
    }

    return next.handle(req).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          switch (error.status || 0) {
            // Connection error, delay by tries * 500ms and try again 5 times
            case 0:
            case -1:
              if (count < 5) {
                return timer(count * 500).pipe(
                  first(),
                  switchMap(() => this.sendRequest(req, next, token, count + 1))
                );
              }
              break;
            /* Authentication error, if this is the first try,
            there is a token and this is not an auth request, refresh and try again*/
            case 401:
              this.logut401();
              //  if (!count && req.url.indexOf(`${Config.baseUrl}/auth`) === -1) {
              // return this.handle401Response(req, next, count, error);
              // }
              break;
          }
        }

        // Forward all remaining errors
        return throwError(error);
      })
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.indexOf(Config.baseUrl) !== 0) {
      return next.handle(req);
    }

    let headers = req.headers;

    if (this.locale && !headers.has('X-Locale')) {
      headers = headers.set('X-Locale', this.locale);
    }

    // New global headers here
    headers = headers
      .set('X-App-Version', Config.appVersion);

    req = req.clone({headers});

    let observable: Observable<string>;
    if (req.url.indexOf(`${Config.baseUrl}/auth`) !== 0) {
      observable = this.tokenStoreService.token.pipe(
        map(token => token && token.access_token)
      );
    } else {
      observable = of(null);
    }

    return observable.pipe(
      switchMap((token: string) => this.sendRequest(req, next, token))
    );
  }
}
